import React, { useMemo, useState, Component } from 'react';
import { Search, Filter, X, TrendingUp, AlertTriangle, CheckCircle, FileSearch } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { ScanCard } from '../components/Dashboard/ScanCard';
import { useAuth } from '../contexts/AuthContext';
import { useScans } from '../hooks/useScans';
type FilterType = 'all' | 'high' | 'medium' | 'low';
export function AllScansPage() {
  const {
    user
  } = useAuth();
  const {
    scans,
    loading
  } = useScans(user?.id);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  // Calculate stats
  const stats = useMemo(() => {
    const completed = scans.filter(s => s.status === 'completed');
    const highRisk = completed.filter(s => s.risk_level === 'high').length;
    const mediumRisk = completed.filter(s => s.risk_level === 'medium').length;
    const lowRisk = completed.filter(s => s.risk_level === 'low').length;
    return {
      total: scans.length,
      highRisk,
      mediumRisk,
      lowRisk,
      avgScore: completed.length > 0 ? Math.round(completed.reduce((sum, s) => sum + (s.score || 0), 0) / completed.length) : null
    };
  }, [scans]);
  // Filter scans
  const filteredScans = useMemo(() => {
    let filtered = scans;
    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(scan => scan.title.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    // Apply risk level filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(scan => scan.risk_level === activeFilter);
    }
    return filtered;
  }, [scans, searchQuery, activeFilter]);
  const filters: {
    value: FilterType;
    label: string;
    icon: React.ReactNode;
    color: string;
  }[] = [{
    value: 'all',
    label: 'All Scans',
    icon: <Filter size={14} data-id="element-3398" />,
    color: 'gray'
  }, {
    value: 'high',
    label: 'High Risk',
    icon: <AlertTriangle size={14} data-id="element-3399" />,
    color: 'red'
  }, {
    value: 'medium',
    label: 'Medium Risk',
    icon: <TrendingUp size={14} data-id="element-3400" />,
    color: 'orange'
  }, {
    value: 'low',
    label: 'Low Risk',
    icon: <CheckCircle size={14} data-id="element-3401" />,
    color: 'green'
  }];
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-3402">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-3403" />

      <WebsiteHeader data-id="element-3404" />

      <div className="max-w-5xl mx-auto w-full relative z-10" data-id="element-3405">
        <main className="px-6 pt-12 pb-24" data-id="element-3406">
          {/* Page Header */}
          <div className="mb-12" data-id="element-3407">
            <div className="inline-flex items-center gap-2 mb-4 bg-[#FF0000]/10 border border-[#FF0000]/30 px-3 py-1" data-id="element-3408">
              <FileSearch size={14} className="text-[#FF0000]" data-id="element-3409" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-3410">
                Case Files
              </span>
            </div>
            <h1 className="text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-3411">
              All Scans
            </h1>
            <p className="text-gray-400 font-mono text-sm max-w-xl" data-id="element-3412">
              View and manage all your conversation analyses. Access historical
              data and track patterns over time.
            </p>
          </div>

          {/* Stats Summary */}
          {!loading && scans.length > 0 && <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-id="element-3413">
              <StatBox label="Total Scans" value={stats.total} color="gray" data-id="element-3414" />
              <StatBox label="High Risk" value={stats.highRisk} color="red" data-id="element-3415" />
              <StatBox label="Medium Risk" value={stats.mediumRisk} color="orange" data-id="element-3416" />
              <StatBox label="Low Risk" value={stats.lowRisk} color="green" data-id="element-3417" />
            </div>}

          {/* Search Bar */}
          <div className="relative mb-8 group" data-id="element-3418">
            <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 group-focus-within:text-[#FF0000] transition-colors" data-id="element-3419" />
            <input type="text" placeholder="Search by conversation title..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-12 py-4 bg-[#0a0a0a] border border-gray-800 
                       focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000]
                       text-white placeholder-gray-600 font-mono text-sm transition-all" data-id="element-3420" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 transform -translate-y-1/2 
                         text-gray-500 hover:text-white transition-colors" data-id="element-3421">
                <X size={20} data-id="element-3422" />
              </button>}

            {/* Corner accents for search */}
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600 group-focus-within:border-[#FF0000] transition-colors pointer-events-none" data-id="element-3423" />
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600 group-focus-within:border-[#FF0000] transition-colors pointer-events-none" data-id="element-3424" />
            <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600 group-focus-within:border-[#FF0000] transition-colors pointer-events-none" data-id="element-3425" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600 group-focus-within:border-[#FF0000] transition-colors pointer-events-none" data-id="element-3426" />
          </div>

          {/* Filter Chips */}
          <div className="flex flex-wrap gap-3 mb-12" data-id="element-3427">
            {filters.map(filter => {
            const isActive = activeFilter === filter.value;
            const count = filter.value === 'all' ? stats.total : filter.value === 'high' ? stats.highRisk : filter.value === 'medium' ? stats.mediumRisk : stats.lowRisk;
            let activeClass = '';
            if (isActive) {
              if (filter.color === 'red') activeClass = 'bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]';else if (filter.color === 'orange') activeClass = 'bg-orange-600/10 text-orange-500 border-orange-600';else if (filter.color === 'green') activeClass = 'bg-green-600/10 text-green-500 border-green-600';else activeClass = 'bg-gray-800 text-white border-gray-700';
            } else {
              activeClass = 'bg-transparent text-gray-500 border-gray-800 hover:border-gray-600 hover:text-gray-300';
            }
            return <button key={filter.value} onClick={() => setActiveFilter(filter.value)} className={`flex items-center gap-2 px-4 py-2 border font-mono text-xs uppercase tracking-wider transition-all ${activeClass}`} data-id="element-3428">
                  {filter.icon}
                  <span data-id="element-3429">{filter.label}</span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold border ${isActive ? 'bg-black/20 border-current opacity-50' : 'bg-gray-900 text-gray-500 border-gray-800'}`} data-id="element-3430">
                    {count}
                  </span>
                </button>;
          })}
          </div>

          {/* Results Count */}
          {!loading && <div className="mb-6 flex items-center gap-2" data-id="element-3431">
              <div className="w-1.5 h-1.5 bg-[#FF0000] rounded-full animate-pulse" data-id="element-3432"></div>
              <p className="text-xs font-mono text-gray-500 uppercase tracking-widest" data-id="element-3433">
                {filteredScans.length === 0 ? 'No records found' : `Displaying ${filteredScans.length} ${filteredScans.length === 1 ? 'record' : 'records'}`}
                {searchQuery && ` matching "${searchQuery}"`}
              </p>
            </div>}

          {/* Content */}
          {loading ? <div className="space-y-4" data-id="element-3434">
              {[1, 2, 3].map(i => <div key={i} className="bg-[#0a0a0a] p-6 border border-gray-800 animate-pulse relative" data-id="element-3435">
                  <div className="flex items-center justify-between" data-id="element-3436">
                    <div className="flex-1" data-id="element-3437">
                      <div className="h-5 bg-gray-800 w-2/3 mb-3" data-id="element-3438"></div>
                      <div className="h-4 bg-gray-900 w-1/2" data-id="element-3439"></div>
                    </div>
                    <div className="w-16 h-16 bg-gray-800" data-id="element-3440"></div>
                  </div>
                </div>)}
            </div> : filteredScans.length === 0 ? <div className="bg-[#0a0a0a] p-12 border border-gray-800 text-center relative" data-id="element-3441">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-3442" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-3443" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-3444" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-3445" />

              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-900 mb-6 border border-gray-800" data-id="element-3446">
                <Search className="w-10 h-10 text-gray-600" data-id="element-3447" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 uppercase tracking-wide" data-id="element-3448">
                {searchQuery || activeFilter !== 'all' ? 'No scans found' : 'No scans yet'}
              </h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto font-mono text-sm" data-id="element-3449">
                {searchQuery ? `No scans match "${searchQuery}". Try a different search term.` : activeFilter !== 'all' ? `No ${activeFilter} risk scans found. Try a different filter.` : 'Upload your first conversation to get started with toxicity detection.'}
              </p>
              {searchQuery || activeFilter !== 'all' ? <button onClick={() => {
            setSearchQuery('');
            setActiveFilter('all');
          }} className="inline-flex items-center gap-2 bg-gray-800 text-white px-6 py-3 
                           hover:bg-gray-700 transition-all font-mono text-xs uppercase tracking-wider border border-gray-700" data-id="element-3450">
                  <X size={16} data-id="element-3451" />
                  Clear Filters
                </button> : <a href="/upload" className="inline-flex items-center gap-2 bg-[#FF0000] text-white px-6 py-3 
                           hover:bg-red-600 transition-all font-bold uppercase tracking-wider shadow-[0_0_15px_rgba(255,0,0,0.3)]" data-id="element-3452">
                  Upload Now
                </a>}
            </div> : <div className="space-y-4" data-id="element-3453">
              {filteredScans.map(scan => <ScanCard key={scan.id} scanId={scan.id} title={scan.title} timeAgo={getTimeAgo(scan.created_at)} score={scan.score || 0} riskLevel={getRiskLevelText(scan.risk_level)} messages={scan.message_count || 0} tags={getTopPatterns(scan.patterns)} status={scan.status} patterns={scan.patterns || []} summary={(scan.analysis as any)?.summary} isUnlocked={scan.is_unlocked} data-id="element-3454" />)}
            </div>}
        </main>
      </div>
    </div>;
}
// Helper Components
const StatBox = ({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: string;
}) => {
  let colorClass = 'text-white';
  if (color === 'red') colorClass = 'text-[#FF0000]';else if (color === 'orange') colorClass = 'text-orange-500';else if (color === 'green') colorClass = 'text-green-500';
  return <div className="bg-[#0a0a0a] p-4 border border-gray-800 relative group hover:border-gray-700 transition-colors" data-id="element-3455">
      {/* Corner Brackets */}
      <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-3456" />
      <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-3457" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-3458" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-3459" />

      <div className={`text-3xl font-black mb-1 font-mono ${colorClass}`} data-id="element-3460">
        {value}
      </div>
      <div className="text-[10px] text-gray-500 uppercase tracking-widest font-mono" data-id="element-3461">
        {label}
      </div>
    </div>;
};
function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
}
function getRiskLevelText(riskLevel: 'low' | 'medium' | 'high' | null): string {
  if (!riskLevel) return 'Processing';
  return riskLevel === 'high' ? 'High Risk' : riskLevel === 'medium' ? 'Medium Risk' : 'Low Risk';
}
// FIX: Handle both string[] and Pattern[] patterns
function getTopPatterns(patterns: any[] | null): string[] {
  if (!patterns || !Array.isArray(patterns) || patterns.length === 0) return [];
  // Handle both string patterns and object patterns
  return patterns.slice(0, 2).map(p => {
    if (typeof p === 'string') return p;
    if (typeof p === 'object' && p !== null && p.type) return p.type;
    return 'Unknown';
  });
}